import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy, limit, startAfter, QueryConstraint, DocumentData, QueryDocumentSnapshot, OrderByDirection } from 'firebase/firestore';
import { db } from '../firebase';

interface WhereOptions {
    field: string;
    operator: '==' | '<' | '<=' | '>' | '>=' | '!=' | 'array-contains' | 'array-contains-any' | 'in' | 'not-in';
    value: any;
}

interface OrderByOptions {
    field: string;
    direction?: OrderByDirection;
}

export const usePaginatedCollection = <T extends { id: string }>(
    collectionName: string, 
    whereOptions: WhereOptions | WhereOptions[] | null,
    orderByOptions: OrderByOptions,
    itemsPerPage: number
) => {
    const [data, setData] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMore, setHasMore] = useState(true);

    const fetchInitialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const constraints: QueryConstraint[] = [
                orderBy(orderByOptions.field, orderByOptions.direction),
                limit(itemsPerPage)
            ];

            if (whereOptions) {
                if (Array.isArray(whereOptions)) {
                    whereOptions.forEach(option => {
                        constraints.unshift(where(option.field, option.operator, option.value));
                    });
                } else {
                    constraints.unshift(where(whereOptions.field, whereOptions.operator, whereOptions.value));
                }
            }

            const q = query(collection(db, collectionName), ...constraints);

            const snapshot = await getDocs(q);
            const fetchedData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T));
            
            setData(fetchedData);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(snapshot.docs.length === itemsPerPage);

        } catch (err) {
            console.error(`Error fetching initial data from ${collectionName}:`, err);
        } finally {
            setIsLoading(false);
        }
    }, [collectionName, itemsPerPage, orderByOptions.field, orderByOptions.direction, JSON.stringify(whereOptions)]);
    
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore) return;

        setIsLoadingMore(true);
        try {
             const constraints: QueryConstraint[] = [
                orderBy(orderByOptions.field, orderByOptions.direction),
                startAfter(lastVisible),
                limit(itemsPerPage)
            ];

            if (whereOptions) {
                 if (Array.isArray(whereOptions)) {
                    whereOptions.forEach(option => {
                        constraints.unshift(where(option.field, option.operator, option.value));
                    });
                } else {
                    constraints.unshift(where(whereOptions.field, whereOptions.operator, whereOptions.value));
                }
            }
            
            const q = query(collection(db, collectionName), ...constraints);
            const snapshot = await getDocs(q);
            
            const newDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T));
            setData(prev => [...prev, ...newDocs]);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(snapshot.docs.length === itemsPerPage);

        } catch (err) {
             console.error(`Error loading more data from ${collectionName}:`, err);
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMore, isLoadingMore, lastVisible, collectionName, itemsPerPage, orderByOptions.field, orderByOptions.direction, JSON.stringify(whereOptions)]);

    return { data, isLoading, isLoadingMore, loadMore, hasMore, refetch: fetchInitialData };
};